import type { FieldHelpContent } from "@/components/ui/field-help";

/** Curated Org settings help ids — plan/46. */
export const orgSettingsHelp = {
  taxPriceMode: {
    title: "Tax price mode",
    summary:
      "Whether shelf and line prices already include tax, or tax is added on top.",
    example:
      "Inclusive at 12%: a ₱100 item stays ₱100 at the till; tax portion is inside that price.",
    useCases: [
      "PH retail / VAT-inclusive price lists — keep Inclusive.",
      "Wholesale quotes that show tax separately — use Exclusive.",
    ],
  },
  taxRatePct: {
    title: "Default tax rate",
    summary: "Org default % applied on new POS tickets and orders when a line doesn’t override.",
    example: "12 for standard VAT; 0 if you sell mostly zero-rated or exempt items.",
    useCases: [
      "Changing the default does not rewrite old tickets — only new docs.",
    ],
  },
  discountSoftCapPct: {
    title: "Discount soft cap",
    summary:
      "Floor roles get a warning (and may need approval) when a discount goes above this %.",
    example: "Cap 10%: a cashier entering 15% is warned or must request an override.",
    useCases: [
      "Managers with override allow can confirm and proceed; others request approval.",
    ],
  },
  allowOversell: {
    title: "Allow oversell",
    summary:
      "When on (default), selling or delivering below recorded on-hand warns and continues — books often lag the floor.",
    example:
      "On-hand 2, sell 5: with Allow on → proceed + audit; with Allow off → hard block.",
    useCases: [
      "Turn off only when stock counts are trusted and you want a hard stop.",
    ],
  },
  lowStockThreshold: {
    title: "Low-stock threshold",
    summary:
      "Org default for Home Low stock and Inventory “Low stock”. Products may override this in Advanced.",
    example:
      "0 (default): only zero / negative on-hand. Set 5 to warn when on-hand is 5 or less (unless the product sets its own).",
    useCases: [
      "Leave at 0 for the prior “out of stock” signal.",
      "Raise when you reorder before hitting zero.",
      "Override per SKU on the product Advanced section.",
    ],
  },
  requireSalesInvoices: {
    title: "Require invoices",
    summary:
      "Separate toggles for AR (sales) and AP (purchase). When on, settlement must apply to an open invoice — not the raw order balance.",
    example:
      "SO open with no SI: Collect is blocked until you Invoice, or a manager overrides. Same on PO → PI for Pay.",
    useCases: [
      "Leave off for floor shops that settle on the order.",
      "Turn on when AR/AP must match formal bills.",
    ],
  },
  requireSalesInTransit: {
    title: "Require in-transit",
    summary:
      "When on, sales deliver on **delivery** (or unset) orders needs an open in-transit shipment first — same soft-warn / override as require SI. **Pickup** orders are exempt.",
    example:
      "Delivery SO with no submitted shipment: Deliver is blocked until In transit, or a manager overrides. Pickup SO: Mark picked up freely.",
    useCases: [
      "Leave off when shops deliver straight from the order.",
      "Turn on when dispatch must be recorded before courier stock leaves.",
    ],
  },
  requirePurchaseInTransit: {
    title: "Require inbound in-transit",
    summary:
      "When on, goods receipt needs an open inbound in-transit shipment first — same soft-warn / override as sales require in-transit. No pickup exemption on PO.",
    example:
      "PO with no submitted inbound shipment: Receive is blocked until In transit, or a manager overrides.",
    useCases: [
      "Leave off when shops receive straight from the purchase order.",
      "Turn on when inbound logistics must be recorded before warehouse put-away.",
    ],
  },
  docDateBackdate: {
    title: "Document date backdating",
    summary:
      "How far before today a doc date may be set, and whether over-limit is a soft warn or hard reject.",
    example:
      "Max 7 days, soft: dating an SO 10 days back warns; hard: blocked unless override.",
    useCases: [
      "Match a supplier’s invoice date on a PO without opening unlimited history edits.",
    ],
  },
  letterhead: {
    title: "Letterhead",
    summary:
      "Address, TIN, contacts, and logo used on formal prints (orders, invoices, statements).",
    example:
      "Upload a PNG logo and set TIN + address so SOA and sales invoices look like your paper.",
    useCases: [
      "Leave fields empty if you only need the company name on slips for now.",
      "Optional registration / permit notes for PH authority-to-print style lines — not a full BIR module.",
    ],
  },
} as const satisfies Record<string, FieldHelpContent>;
